import { useNavigate, useParams } from 'react-router';
import { ScreenSkeleton } from '../../../../components/atoms/ScreenSkeleton';
import {
  CategoryType,
  EquipmentType,
  ObservableLevelsEnum,
} from '../../../../data/enums';
import { Button } from '../../../../components/atoms/Button';
import { EquipmentPartModel } from '../../../../data/types';
import { ScreenHeading } from '../../../../components/molecules/ScreenHeading';
import { ActionStatusBar } from '../../../../components/molecules/ActionStatusBar';
import { ScreenObservationLayout } from '../../../../components/templates/ScreenObservationLayout';
import { VisualCondition } from '../../../../components/atoms/RadioButtonGroupField';
import { Label } from '../../../../components/atoms/Label';
import PhotoSelector from '../../../../components/atoms/PhotoSelector';
import { RichTextField } from '../../../../components/atoms/RichTextField';
import { ErrorBanner } from '../../../../components/atoms/ErrorBanner';
import {
  SliderRollerAssemblyConstants,
  useSliderRollerHook,
} from '../../../../store/sliderRollerAssembly';
import CheckboxWithLabel from '../../../../components/atoms/Checkbox';
import { ArrowLeft } from 'lucide-react';
import { tocUndercarriageScreenPathValues } from '../tocUndercarriageScreenPath';
import { ObservableItemIdentifier } from '../../../../api-contracts/other/ObservableItemIdentifier';
import { MdHelpers } from '../../../../helpers/mdHelpers';
import useInspectionCategoryType from '../../../../hooks/useInspectionCategoryType';
import { useInspectionCached } from '../../../../hooks/useInspectionCached';
import { ObservationDraftScope } from '../../../../components/ObservationDraftScope';

interface LoadRollerScreenProps {
  heading: string;
  subheading: string;
  equipmentPart: EquipmentPartModel;
}

export const SliderScreen = ({
  equipmentPart,
  heading,
  subheading,
}: LoadRollerScreenProps) => {
  const { externalId } = useParams();
  const navigate = useNavigate();

  const { inspection } = useInspectionCached(externalId);

  const undercarriageCategory = useInspectionCategoryType({
    inspection,
  });

  const { Undercarriage } = ObservableItemIdentifier.Identifiers;
  const { error, listWearIndicators, markAsComplete } = useSliderRollerHook({
    equipmentPart,
    externalId,
    inspectionCategoryTypeId: undercarriageCategory?.externalId,
  });

  if (!externalId) {
    return <ErrorBanner message="Missing inspection data" />;
  }

  if (!equipmentPart || !undercarriageCategory) {
    return <ScreenSkeleton fields={4} />;
  }

  if (error) {
    return <ErrorBanner message={error} />;
  }

  return (
    <ObservationDraftScope inspectionId={externalId}>
      <div>
        <ScreenHeading
          eyebrow={heading
            .replace(/-/g, ' ')
            .replace(/^./, (c) => c.toUpperCase())}
          title={subheading}
          tooltipKey={'undercarriage-roller-assemblies-sliders-screen-header'}
        />

        <ActionStatusBar
          inspectionId={externalId!}
          inspectionCategoryTypeId={undercarriageCategory?.externalId ?? ''}
          dataLevel={ObservableLevelsEnum.equipmentPart}
          entities={[
            {
              entityId: equipmentPart.id,
              entityExternalId: equipmentPart.externalId,
            },
          ]}
          paths={[]}
          pathsLike={
            tocUndercarriageScreenPathValues.find(
              (path) =>
                path.partName === SliderRollerAssemblyConstants.partName,
            )?.anyPaths ?? []
          }
          showPercentage={false}
        />

        <ScreenObservationLayout>
          <VisualCondition
            inspectionId={externalId!}
            inspectionCategoryTypeId={undercarriageCategory?.externalId ?? ''}
            dataLevel={ObservableLevelsEnum.equipmentPart}
            entityId={equipmentPart.id}
            entityExternalId={equipmentPart.externalId}
            divider
            path={Undercarriage.Roller.VisualCondition}
          />

          <Label titleType="fieldTitle">Wear Indicators</Label>
          {listWearIndicators.map((e) => (
            <CheckboxWithLabel
              key={e.path}
              inspectionId={externalId}
              inspectionCategoryTypeId={undercarriageCategory?.externalId ?? ''}
              dataLevel={ObservableLevelsEnum.equipmentPart}
              entityId={equipmentPart.id}
              entityExternalId={equipmentPart.externalId}
              infoTooltipKey={`undercarriage-roller-assemblies-sliders-${MdHelpers.normaliseTooltipKeyComponent(e.label)}`}
              {...e}
            />
          ))}

          <PhotoSelector
            inspectionId={externalId!}
            inspectionCategoryTypeId={undercarriageCategory?.externalId ?? ''}
            dataLevel={ObservableLevelsEnum.equipmentPart}
            title="Photos"
            entityId={equipmentPart.id}
            entityExternalId={equipmentPart.externalId}
            path={Undercarriage.Roller.Photo}
          />

          <RichTextField
            inspectionId={externalId!}
            inspectionCategoryTypeId={undercarriageCategory?.externalId ?? ''}
            dataLevel={ObservableLevelsEnum.equipmentPart}
            entityId={equipmentPart.id}
            entityExternalId={equipmentPart.externalId}
            path={Undercarriage.Roller.Comments}
            label="Comments"
            placeholder="Enter your comments here..."
            className="pt-4"
          />
        </ScreenObservationLayout>

        <div className="flex items-center justify-center p-4">
          <Button
            variant="secondary"
            prefixIcon={<ArrowLeft color="white" />}
            onClick={() => {
              try {
                markAsComplete?.();
              } catch (_) {
              } finally {
                navigate(-1);
              }
            }}
          >
            {SliderRollerAssemblyConstants.submit}
          </Button>
        </div>
      </div>
    </ObservationDraftScope>
  );
};
